import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SunEditor from '../../components/SunEditor';
import SunEditorCore from 'suneditor/src/lib/core';
import juice from 'juice';
import 'suneditor/dist/css/suneditor.min.css';
import sunEditorCss from '../../css/suneditor';
import useCheckPermission from '../../hooks/useCheckPermission';
import { EmailTemplateType } from '../../types/EmailTemplateType';

const EmailTemplateForm = (props: any) => {
  const { entity, onSubmit, errors, handleCancel, readonly = false } = props;
  const editor = useRef<SunEditorCore>(null!);
  const parkingEnabled = useSelector(
    (state: any) => state.app.featureFlags?.parkingEnabled
  );

  const getSunEditorInstance = (sunEditor: SunEditorCore) => {
    editor.current = sunEditor;
  };

  const templateTypes = useMemo(() => {
    const types = [
      EmailTemplateType.ReportEmail,
      EmailTemplateType.NotificationEmail,
    ];
    if (parkingEnabled) {
      types.push(EmailTemplateType.ParkingEmail);
    }
    return types;
  }, [parkingEnabled]);

  const { checkPermission } = useCheckPermission();

  const [suneditorCss, setSuneditorCss] = useState<string>(sunEditorCss);

  return (
    <Formik
      initialValues={entity}
      enableReinitialize={true}
      validate={(values) => {
        const errors: any = {};

        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values, setSubmitting);
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        setFieldValue,
        handleChange,
        errors: validationErrors,
      }) => (
        <Form>
          {' '}
          <Paper>
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "left",
                p: 5
              }}>
              <h2>
                Email sablon{' '}
                {readonly
                  ? 'megtekintése'
                  : values.id !== 0
                  ? 'szerkesztése'
                  : 'létrehozása'}
              </h2>
              <Grid size={12} sx={{
                pt: 5
              }}>
                <Box sx={{
                  color: "red"
                }}>
                  {errors?.map((error: any) => <li>{error}</li>) ?? []}
                </Box>
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.cc}
                  onChange={handleChange}
                  label="Másolat (CC)"
                  name="cc"
                  disabled={readonly}
                  fullWidth
                  error={!!touched.cc && !!validationErrors.cc}
                  helperText={
                    !!touched.cc &&
                    !!validationErrors.cc &&
                    (validationErrors.cc as string)
                  }
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.replyTo}
                  onChange={handleChange}
                  label="Válasz cím"
                  name="replyTo"
                  disabled={readonly}
                  fullWidth
                  error={!!touched.replyTo && !!validationErrors.replyTo}
                  helperText={
                    !!touched.replyTo &&
                    !!validationErrors.replyTo &&
                    (validationErrors.replyTo as string)
                  }
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.name}
                  onChange={handleChange}
                  label="Név"
                  name="name"
                  disabled={readonly}
                  fullWidth
                  error={!!touched.name && !!validationErrors.name}
                  helperText={
                    !!touched.name &&
                    !!validationErrors.name &&
                    (validationErrors.name as string)
                  }
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.subject}
                  onChange={handleChange}
                  label="Tárgy"
                  name="subject"
                  disabled={readonly}
                  fullWidth
                  error={!!touched.subject && !!validationErrors.subject}
                  helperText={
                    !!touched.subject &&
                    !!validationErrors.subject &&
                    (validationErrors.subject as string)
                  }
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  disablePortal
                  id="type"
                  disabled={readonly}
                  value={values?.type ?? ''}
                  onChange={(event, value) => {
                    setFieldValue('type', value);
                  }}
                  getOptionLabel={(option) =>
                    option === EmailTemplateType.ReportEmail
                      ? 'Bejelentés'
                      : option === EmailTemplateType.NotificationEmail
                      ? 'Értesítés'
                      : option === EmailTemplateType.ParkingEmail
                      ? 'Parkolás'
                      : ''
                  }
                  options={templateTypes?.map((g) => g) ?? []}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!!touched.type && !!validationErrors.type}
                      helperText={
                        !!touched.type &&
                        !!validationErrors.type &&
                        (validationErrors.type as string)
                      }
                      fullWidth
                      label="Típus"
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.description}
                  onChange={handleChange}
                  label="Leírás"
                  name="description"
                  disabled={readonly}
                  fullWidth
                  error={
                    !!touched.description && !!validationErrors.description
                  }
                  helperText={
                    !!touched.description &&
                    !!validationErrors.description &&
                    (validationErrors.description as string)
                  }
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <SunEditor
                  getSunEditorInstance={getSunEditorInstance}
                  width="100%"
                  height="500px"
                  setContents={values.body}
                  placeholder="Please type here..."
                  disable={readonly}
                  defaultValue={values.body}
                  onChange={(content: string) => {
                    var result = juice(
                      `<div class="sun-editor-editable">${content}</div>`,
                      {
                        extraCss: suneditorCss,
                      }
                    );
                    setFieldValue('body', result);
                  }}
                  setOptions={{
                    buttonList: [
                      ['undo', 'redo'],
                      ['font', 'fontSize', 'formatBlock'],
                      [
                        'bold',
                        'underline',
                        'italic',
                        'strike',
                        'subscript',
                        'superscript',
                      ],
                      ['fontColor', 'hiliteColor'],
                      ['removeFormat'],
                      '/', // line break
                      ['outdent', 'indent'],
                      ['align', 'horizontalRule', 'list', 'table'],
                      ['link', 'image', 'video'],
                      ['fullScreen', 'showBlocks', 'codeView'],
                      ['preview', 'print'],
                      ['math'],
                    ],
                    katex: 'window.katex',
                    templates: [],
                  }}
                />
              </Grid>
              {values.type === EmailTemplateType.ParkingEmail && (
                <>
                  {' '}
                  <Grid size={12}>
                    <Typography variant="subtitle1">
                      {
                        '* "{{downloadButton}}" helyére kerül a nyugta letöltése gomb.'
                      }
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle1">
                      {'* "{{parkingZone}}" helyére kerül a Parkolózóna.'}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle1">
                      {
                        '* "{{parkingTime}}" helyére kerül a parkolás időtartama.'
                      }
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle1">
                      {
                        '* "{{licensePlate}}" helyére kerül a rendszám.'
                      }
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid
                container
                sx={{
                  pt: 3,
                  justifyContent: "left"
                }}>
                {!readonly && (
                  <Grid sx={{
                    p: 1
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        onSubmit(values);
                      }}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid sx={{
                  p: 1
                }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  );
};

export default EmailTemplateForm;
